<template>
    <div class="editor">
        <TransitionEffect>
            <Dialog
                v-if="deletePostDialog"
                appearance="red"
                primaryIcon="delete"
                @primary="removePost"
                @secondary="deletePostDialog=false"
                @close="deletePostDialog=false">
                <template slot="header">{{ siteContent.deleteEntryQuestion }}</template>
                <template slot="body">{{ siteContent.thinkTwice }}</template>
                <template slot="secondary">{{ siteContent.cancel }}</template>
                <template slot="primary">{{ siteContent.delete }}</template>
            </Dialog>
        </TransitionEffect>

        <div class="toggleView">
            <a :class="['toggleItem', { isToggleActive: isEnglishVisible }]"
                @click="isEnglishVisible=true">
                {{ siteContent.english}}
            </a>
            <a :class="['toggleItem', { isToggleActive: !isEnglishVisible }]"
                @click="isEnglishVisible=false">
                {{ siteContent.hungarian }}
            </a>
        </div>
            
        <transition name="fadeHorizontally">
            <div class="englishFields" v-show="isEnglishVisible">
                <TextField class="field"
                    id="titleEn"
                    ref="titleEn"
                    size="large"
                    v-model="title.en"
                    :label="siteContent.title"/>
                <TextField class="field"
                    id="summaryEn"
                    ref="summaryEn"
                    size="medium"
                    v-model="summary.en"
                    :label="siteContent.prologue"
                    type="textarea"
                    :isCharCountOn="true">{{ summary.en }}</TextField>
                <quill-editor
                    ref="quillEditorEn"
                    v-model="contentEn"
                    :options="editorOptions"/>
            </div>
        </transition>

        <transition name="fadeHorizontally">
            <div class="hungarianFields" v-show="!isEnglishVisible">
                <TextField class="field"
                    id="titleHu"
                    ref="titleHu"
                    size="large"
                    v-model="title.hu"
                    :label="siteContent.title"/>
                <TextField class="field"
                    id="summaryHu"
                    ref="summaryHu"
                    size="medium"
                    v-model="summary.hu"
                    :label="siteContent.prologue"
                    type="textarea"
                    :isCharCountOn="true">{{ summary.hu }}</TextField>
                <quill-editor
                    ref="quillEditorHu"
                    v-model="contentHu"
                    :options="editorOptions"/>
            </div>
        </transition>

        <DatePicker class="field dateField"
            :date="currentPost.uploadTime"
            :label="siteContent.uploadTime"
            @setNewDate="setNewDate"/>

        <TextField class="field relatedEntry"
            id="relatedEntry"
            ref="relatedEntry"
            size="medium"
            v-model="relatedEntry"
            :label="siteContent.relatedEntry"/>

        <div class="publishPost"
            v-if="permissions.isAdmin">
            <Toggle class="toggleOption"
                ref="visibility"
                v-model="visibility"
                :label="siteContent.published">
            </Toggle>
        </div>

        <div class="buttonsWrapper">
            <div class="buttons">
                <Button class="deleteButton"
                    prefixIcon="delete"
                    appearance="red"
                    size="small"
                    @click="deletePostDialog=true">
                    <span class="deleteText">{{ siteContent.delete }}</span>
                </Button>

                <div class="editOptions">
                    <Button class="button"
                        appearance="secondary"
                        size="small"
                        prefixIcon="arrow_left"
                        @click="$emit('canceledit')">
                        {{ siteContent.cancel }}
                    </Button>
                    <Button class="button"
                        @click="update"
                        prefixIcon="save"
                        size="small">
                        {{ siteContent.update }}
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions } from 'vuex'

    import 'quill/dist/quill.snow.css'
    import { quillEditor } from 'vue-quill-editor'

    export default {
        name: 'PostEditor',
        components: {
            quillEditor
        },
        props: {
            currentPost: Object,
            currentUser: Object,
            id: String
        },
        data () {
            return {
                deletePostDialog: false,
                isEnglishVisible: true,
                title: {
                    en: this.currentPost.title.en,
                    hu: this.currentPost.title.hu
                },
                summary: {
                    en: this.currentPost.summary.en,
                    hu: this.currentPost.summary.hu,
                },
                deltaEn: this.currentPost.deltaEn,
                deltaHu: this.currentPost.deltaHu,
                relatedEntry: this.currentPost.relatedEntry,
                visibility: this.currentPost.visibility,
                uploadTime: this.currentPost.uploadTime,
                updatedUploadTime: null,
                contentEn: '',
                contentHu: '',
                editorOptions: {
                    theme: 'snow',
                    placeholder: 'The space is all yours!',
                    scrollingContainer: 'body',
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['link', 'image', 'video'], ['blockquote'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'align': [] }],
                            [{ 'header': [2, 3, 4, 5, 6, false] }]
                        ]
                    }
                }
            }
        },
        methods: {
            ...mapActions('blog',['updatePost','deletePost']),

            forceUpdateTextFields() {
                const summaryElementEn = this.$refs.summaryEn.$el.querySelector('textarea')
                const summaryElementHu = this.$refs.summaryHu.$el.querySelector('textarea')
                const event = new Event('keyup')

                summaryElementEn.dispatchEvent(event)
                summaryElementHu.dispatchEvent(event)
            },

            setNewDate(e) {
                this.updatedUploadTime = e
            },

            async update() {
                this.$nextTick(async () => {
                    var updatedTitle = {
                        hu: this.$refs.titleHu.value,
                        en: this.$refs.titleEn.value,
                    }

                    var updatedSummary = { 
                        hu: this.$refs.summaryHu.value,
                        en: this.$refs.summaryEn.value,
                    }

                    var updatedDeltaEn = {
                        deltaEn: this.deltaEn
                    }
                    var updatedDeltaHu = {
                        deltaHu: this.deltaHu
                    }
                    if(this.permissions.isAdmin) {
                        var updatedVisibility = this.$refs.visibility.isEnabled
                    } else {
                        var updatedVisibility = this.visibility
                    }
                    var relatedEntry = this.$refs.relatedEntry.value
                    
                    var postId = this.id
                    var updatedContent = {
                        title: updatedTitle,
                        summary: updatedSummary,
                        visibility: updatedVisibility,
                        relatedEntry: relatedEntry,
                        uploadTime: this.updatedUploadTime
                    }
                    
                    var result = await this.updatePost({ postId, updatedContent, updatedDeltaEn, updatedDeltaHu })
                    
                    if (result) {
                        this.$toasted.show(this.siteContent.entryUpdateSuccess)
                        this.$emit('finishedit')
                    } else {
                        this.$toasted.show(this.siteContent.entryUpdateError)
                    }
                })
            },

            async removePost() {
                var result = await this.deletePost({ 
                        id: this.id,
                        imageUrl: this.currentPost.cover
                    })
                if(result) {
                    this.$toasted.show(this.siteContent.entryDeleteSuccess)
                } else {
                    this.$toasted.show(this.siteContent.entryDeleteError)
                }
                this.$router.push('/blog')
            }
        },
        mounted() {
            this.forceUpdateTextFields()
            this.$refs.quillEditorEn.quill.setContents({
                "ops": this.deltaEn
            })
            this.$refs.quillEditorHu.quill.setContents({
                "ops": this.deltaHu
            })
            
            if(this.preferences.language === 'en') {
                this.isEnglishVisible = true
            } else {
                this.isEnglishVisible = false
            }
        },
        watch: {
            contentEn() {
                this.deltaEn = this.$refs.quillEditorEn.quill.getContents().ops
            },
            contentHu() {
                this.deltaHu = this.$refs.quillEditorHu.quill.getContents().ops
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../../scss/variables';

    .fadeHorizontally-enter-active, .fadeHorizontally-leave-active {
        transition: $transition-eio-60;
    }

    .fadeHorizontally-enter, .fadeHorizontally-leave {
        opacity: 0;
        transform: translateX(5rem);
    }

    .fadeHorizontally-enter-to {
        opacity: 1;
        transform: translateX(0);
    }

    .fadeHorizontally-leave-to {
        opacity: 0;
        transform: translateX(-5rem);
    }

    .field {
        margin-bottom: 2rem;
    }

    .dateField {
        margin-top: 2rem;
    }

    .buttonsWrapper {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        padding: 1rem;
        backdrop-filter: blur(2rem);
        background: var(--color-white-40);
        z-index: 10;
        display: flex;
        justify-content: center;
        border-top: var(--border-grey-300);
    }

    .buttons {
        max-width: 72rem;
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .button {
        &:first-child {
            margin-right: 1rem;
        }
    }

    .editOptions {
        display: flex;
    }

    .toggleView {
        display: flex;
        align-self: center;
        width: 100%;
        padding: 0.4rem 0.2rem;
        margin-bottom: 3rem;
        background: var(--color-grey-100);
        border-radius: 0.5rem;
    }

    .toggleItem {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
        text-align: center;
        padding: 1.25rem;
        margin: 0 0.2rem;
        font-weight: 700;
        font-family: $font-secondary;
        border-radius: 0.5rem;
        color: var(--color-black);
        user-select: none;
        font-size: 1.1rem;
        line-height: 1.75rem;
        
        &:hover {
            background: var(--color-grey-150);
        }

        &:focus {
            outline: 0;
            box-shadow: var(--shadow-focus-s);
        }
    }

    .isToggleActive {
        background: var(--color-grey-150);
        font-size: 1.25rem;
    }

    .publishPost {
        border-radius: 1rem;
        background: var(--color-grey-150);
        padding: 2rem 1.5rem;
        margin-bottom: 3rem;
    }

    @media all and (max-width: 640px) {
        .buttonsWrapper {
            padding: 0.5rem;

            .deleteButton {
                padding-right: 1rem;
            }
        }

        .deleteText {
            display: none;
        }
    }
</style>